import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import classNames from 'classnames';
import { Button } from '../button/button';
import { Spacer } from '../spacer/spacer';
import { FormInlineNotification } from '../form-inline-notification/form-inline-notification';
import { notNil } from '../../core/helpers/is-nil';
import { Dialog } from '../dialog/dialog';
import { Icon } from '../icon/icon';
import { DialogLayoutButton } from '../dialog/dialog-layout-button';
import styles from './yes-no-dialog-with-inline.module.scss';

type Props = {
  titleDialog: string;
  triggerDialog?: React.ReactNode;
  childrenDialog: React.ReactNode;
  handleClick: () => Promise<void>;
  inlineMessage?: string;
  inlineVariant?: 'warning' | 'error';
};

export const YesNoDialogWithInline: React.FC<Props> = ({
  titleDialog,
  triggerDialog,
  childrenDialog,
  handleClick,
  inlineMessage,
  inlineVariant,
}) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const hasInlineNotification = notNil(inlineMessage) && notNil(inlineVariant);

  const handleClose = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  const wrappedHandleClick = async () => {
    await handleClick();
    setIsDialogOpen(false);
  };

  const isWarning = hasInlineNotification && inlineVariant === 'warning';
  const isError = hasInlineNotification && inlineVariant === 'error';

  return (
    <div className={styles.root}>
      <Dialog isOpen={isDialogOpen} title={titleDialog} trigger={triggerDialog} onOpenChange={setIsDialogOpen}>
        <DialogLayoutButton
          buttons={
            <>
              <Button variant="secondary" onClick={handleClose}>
                {t('global.no')}
              </Button>
              <Button onClick={wrappedHandleClick}>{t('global.yes')}</Button>
            </>
          }
        >
          {childrenDialog}
          {hasInlineNotification ? (
            <>
              <Spacer size="06" />
              <FormInlineNotification type={inlineVariant}>{inlineMessage}</FormInlineNotification>
              <Spacer size="06" />
            </>
          ) : null}
        </DialogLayoutButton>
      </Dialog>
      {hasInlineNotification ? (
        <Icon
          size="32"
          variant="triangle-exclamation-mark"
          className={classNames(styles.icon, {
            [styles.warning]: isWarning,
            [styles.error]: isError,
          })}
        />
      ) : null}
    </div>
  );
};
